import { useState, useEffect } from "react";
import { XMarkIcon, KeyIcon } from "../../../ui/icons";
import { useModals } from "../../../hooks/useModals";

const STORAGE_KEY = "palnia_recovery_key_confirmed";
const REMINDER_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours

function shouldShowReminder(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return true;
  const timestamp = Number(stored);
  if (isNaN(timestamp)) return true;
  return Date.now() - timestamp > REMINDER_INTERVAL_MS;
}

interface RecoveryKeyReminderProps {
  readonly force?: boolean;
}

export function RecoveryKeyReminder({ force }: RecoveryKeyReminderProps = {}) {
  const modals = useModals();
  const [visible, setVisible] = useState(() => force || false);

  useEffect(() => {
    if (force) return;
    const timer = setTimeout(() => {
      if (shouldShowReminder()) setVisible(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [force]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-[90] w-[calc(100%-2rem)] max-w-md animate-slide-up">
      <div className="bg-surface border border-amber-500/30 rounded-xl shadow-xl px-4 py-3 flex items-start gap-3">
        <KeyIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink">
            Clé de récupération
          </p>
          <p className="text-xs text-ink-muted mt-0.5 leading-relaxed">
            Avez-vous sauvegardé votre clé de récupération ? Elle est indispensable si vous oubliez votre mot de passe.
          </p>
          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={dismiss}
              className="text-xs font-medium text-accent hover:text-accent/80 transition-colors"
            >
              Oui, j'ai ma clé
            </button>
            <button
              type="button"
              onClick={() => {
                dismiss();
                modals.setSettingsScrollTarget("security");
                modals.setShowSettings(true);
              }}
              className="text-xs font-medium text-amber-500 hover:text-amber-400 transition-colors"
            >
              Non, régénérer
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md hover:bg-surface-alt text-ink-muted"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
